import { useState, useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Plane } from "lucide-react";

function cleanAirportName(name) {
  return name
    .replace(/\s+Airport\s+/gi, " ")
    .replace(/\s+Airport$/gi, "")
    .trim();
}

// Module-level cache — fetched once per session
let _cache = null;
let _promise = null;

async function loadAirports() {
  if (_cache) return _cache;
  if (!_promise) {
    _promise = fetch("https://raw.githubusercontent.com/davidmegginson/ourairports-data/main/airports.csv")
      .then(r => r.text())
      .then(csv => {
        const lines = csv.split("\n");
        const headers = lines[0].split(",").map(h => h.replace(/"/g, "").trim());
        const iataIdx    = headers.indexOf("iata_code");
        const nameIdx    = headers.indexOf("name");
        const cityIdx    = headers.indexOf("municipality");
        const countryIdx = headers.indexOf("iso_country");

        _cache = lines.slice(1)
          .filter(line => line.trim())
          .map(line => {
            const cols = line.match(/(".*?"|[^,]+)(?=,|$)/g) || [];
            const clean = cols.map(c => c.replace(/^"|"$/g, "").trim());
            const originalName = clean[nameIdx] || "";
            const cleanedName  = cleanAirportName(originalName);
            return {
              code:         clean[iataIdx]    || "",
              name:         cleanedName,
              originalName: originalName,
              city:         clean[cityIdx]    || "",
              country:      clean[countryIdx] || "",
            };
          })
          .filter(a =>
            a.code &&
            a.code.length === 3 &&
            /^[A-Z]{3}$/.test(a.code)
          )
          .map(a => ({
            ...a,
            searchText: `${a.code} ${a.name} ${a.originalName} ${a.city} ${a.country}`.toLowerCase(),
          }));
        return _cache;
      });
  }
  return _promise;
}

export default function AirportSearch({
  value,
  onChange,
  placeholder = "Search airport...",
  testId = "airport-search"
}) {
  const [airportList, setAirportList] = useState([]);
  const [query, setQuery]             = useState("");
  const [isOpen, setIsOpen]           = useState(false);
  const [results, setResults]         = useState([]);
  const [selectedAirport, setSelectedAirport] = useState(null);
  const [loading, setLoading]         = useState(true);
  const wrapperRef = useRef(null);

  useEffect(() => {
    loadAirports()
      .then(list => { setAirportList(list); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (value && airportList.length > 0 && !selectedAirport) {
      const airport = airportList.find(a => a.code === value);
      if (airport) {
        setSelectedAirport(airport);
        setQuery(`${airport.name} (${airport.code})`);
      }
    }
  }, [value, airportList]);

  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSearch = (q) => {
    setQuery(q);
    setSelectedAirport(null);
    if (q.length < 2) { setResults([]); setIsOpen(false); return; }
    if (loading) { setIsOpen(true); return; }
    const lower = q.toLowerCase();
    const filtered = airportList.filter(a => a.searchText.includes(lower)).slice(0, 8);
    setResults(filtered);
    setIsOpen(true);
  };

  const handleSelect = (airport) => {
    setSelectedAirport(airport);
    setQuery(`${airport.name} (${airport.code})`);
    setIsOpen(false);
    onChange(airport.code);
  };

  const handleClear = () => {
    setQuery("");
    setSelectedAirport(null);
    setResults([]);
    onChange("");
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <Plane className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400" />
        <Input
          value={query}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => { if (query.length >= 2) setIsOpen(true); }}
          placeholder={loading ? "Loading airports..." : placeholder}
          className="h-12 pl-10 pr-8"
          data-testid={testId}
        />
        {query && (
          <button type="button" onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none">
            ×
          </button>
        )}
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-purple-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {loading ? (
            <div className="px-4 py-3 text-sm text-gray-400 text-center">Loading airports...</div>
          ) : results.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400 text-center">No airports found</div>
          ) : (
            results.map((airport) => (
              <button
                key={airport.code}
                type="button"
                onClick={() => handleSelect(airport)}
                className="w-full px-4 py-3 text-left hover:bg-purple-50 flex items-center gap-3 border-b border-purple-50 last:border-0"
                data-testid={`airport-option-${airport.code}`}
              >
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="font-mono font-bold text-purple-700 text-sm">{airport.code}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-purple-900 truncate">
                    {airport.name} ({airport.code})
                  </p>
                  <p className="text-xs text-purple-400 uppercase tracking-wide">
                    {airport.country} · {airport.code}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
